// Typed client for the lseg_interface backend. Mirrors its camelCase wire
// format 1:1. Attaches the JWT access token to every call and transparently
// retries once on 401 by refreshing the token — the same one-shot refresh
// pattern the interface frontend uses.

import {
  getAccessToken,
  getBackendUrl,
  getRefreshToken,
  setTokens,
  clearTokens,
} from "./auth";

/** An LSEG error the hub reports for one (instrument, field) cell of a table. */
export interface CellErrorEntry {
  instrument: string;
  field: string;
  message: string;
}

/**
 * Realtime FIDs that are not part of an instrument's record (RtGet: the hub
 * snapshot's refresh image has no such key), as opposed to present but blank.
 */
export interface AbsentEntry {
  instrument: string;
  fields: string[];
}

export interface TableResult {
  columns: string[];
  data: any[][];
  index?: any[] | null;
  /** RICs, exactly as sent, that LSEG reported as not found (/data, /history, /snapshot). */
  unresolved?: string[];
  /** Per-cell LSEG errors (/data): the cell shows LSEG's row-field text. */
  errors?: CellErrorEntry[];
  /** RtGet: per RIC, requested FIDs its record does not have; absent when no evidence. */
  absent?: AbsentEntry[];
  /**
   * Plugin-side (batcher): this RIC's slice of a multi-RIC answer was empty
   * (BatchSpec.isEmpty) while another RIC of that answer had something.
   */
  emptyInBatch?: boolean;
}

export interface EvaluateResponse {
  formula: string;
  function: string;
  durationMs: number;
  result: TableResult;
  hubPayload?: Record<string, any> | null;
}

export interface UserResponse {
  id: number;
  username: string;
  email?: string | null;
  isActive: boolean;
  role: string;
  createdAt?: string | null;
  mustChangePassword: boolean;
}

export interface FunctionInfo {
  name: string;
  aliases: string[];
  summary: string;
  signature: string;
  examples: string[];
}

export class ApiError extends Error {
  status: number;
  detail: any;
  /** The server's Retry-After (429/503), in ms; unset when it sent none. */
  retryAfterMs?: number;
  constructor(status: number, message: string, detail?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

// Pull a human-readable message out of the interface's error shapes. Evaluate
// errors come back as {detail: {message, kind, ...}}; auth errors as
// {detail: "Invalid credentials"}.
function messageFromBody(body: any, fallback: string): string {
  const d = body && typeof body === "object" ? body.detail : undefined;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && typeof d.message === "string") return d.message;
  return fallback;
}

async function parseJson(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

interface RequestOpts {
  method?: string;
  body?: any;
  auth?: boolean; // attach bearer token (default true)
  retryOn401?: boolean; // try refresh + retry once (default true when auth)
  afterRefresh?: boolean; // internal: this is the retry that follows a successful refresh
}

export async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { method = "GET", body, auth = true, retryOn401 = true, afterRefresh = false } = opts;
  const base = await getBackendUrl();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth) {
    const token = await getAccessToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e: any) {
    throw new ApiError(0, `Cannot reach the LSEG backend at ${base}. ${e?.message || ""}`.trim());
  }

  if (res.status === 401 && auth && retryOn401) {
    // tryRefresh() resolves true (new tokens stored), false (the refresh token
    // was REJECTED: the session is really over) or throws a transient ApiError
    // (server/network trouble: the tokens are kept, the caller's retry path
    // takes it from here, and a later call refreshes again).
    const refreshed = await tryRefresh();
    if (refreshed) {
      return request<T>(path, { ...opts, retryOn401: false, afterRefresh: true });
    }
    await clearTokens();
    throw new ApiError(401, SESSION_EXPIRED);
  }
  const payload = await parseJson(res);
  if (res.status === 401 && auth && afterRefresh && payload?.detail?.kind !== "hub") {
    // A token minted moments ago was refused too: the session is really over
    // (a transient problem would be a 5xx). Sign out instead of keeping a dead
    // token that refreshes and fails on every later call. A 401 the interface
    // forwards from the hub (kind "hub", e.g. its own API key is refused) says
    // nothing about this session: it falls through with the hub's message.
    await clearTokens();
    throw new ApiError(401, SESSION_EXPIRED);
  }
  if (!res.ok) {
    const err = new ApiError(res.status, messageFromBody(payload, `Request failed (${res.status})`), payload?.detail);
    const wait = retryAfterMs(res);
    if (wait > 0) err.retryAfterMs = wait;
    throw err;
  }
  return payload as T;
}

const SESSION_EXPIRED = "Session expired — sign in again from the LSEG panel.";

/** The refresh answers a bad/expired refresh token or a disabled user with 401 (auth.py); 403 is treated the same. */
function isRefreshRejection(status: number): boolean {
  return status === 401 || status === 403;
}

const REFRESH_TIMEOUT_MS = 15000;
/** Minimum pause after a failed refresh before the server is asked again. */
const REFRESH_BACKOFF_MS = 5000;
const REFRESH_BACKOFF_MAX_MS = 60000;

let refreshInFlight: Promise<boolean> | null = null;
let refreshBackoff: { until: number; error: ApiError } | null = null;
/**
 * Bumped by every sign-in and sign-out. A refresh that started under an older
 * generation must not store its tokens over, or clear, a newer session.
 */
let sessionGeneration = 0;

/** Drop the refresh backoff (a fresh sign-in or a sign-out makes the old failure irrelevant). */
function resetRefreshBackoff(): void {
  refreshBackoff = null;
}

/** A new sign-in or a sign-out: forget the old refresh failure; an in-flight refresh becomes stale. */
function newSession(): void {
  sessionGeneration++;
  resetRefreshBackoff();
}

/**
 * Milliseconds the server should be left alone after a failed refresh (0 when
 * there is no such window). The batcher waits at least this long before its
 * next try, so its retries are not spent inside the back-off.
 */
export function refreshBackoffRemainingMs(): number {
  return refreshBackoff ? Math.max(0, refreshBackoff.until - Date.now()) : 0;
}

function retryAfterMs(res: Response): number {
  const h = Number(res.headers?.get?.("Retry-After"));
  return Number.isFinite(h) && h > 0 ? Math.min(h * 1000, REFRESH_BACKOFF_MAX_MS) : 0;
}

/**
 * Exchange the stored refresh token for new tokens (shared single flight; the
 * stream reconnect uses it too).
 *
 * Resolves true on success and false ONLY when the refresh is rejected as an
 * authentication failure (no refresh token, 401, 403). Anything else — 5xx,
 * 429, other statuses, a network error, a timeout — throws an ApiError and
 * leaves the tokens alone; 5xx/429 keep their status and network errors and
 * timeouts are status 0, which batcher.isTransient() retries. After such a
 * failure the server is not asked again for REFRESH_BACKOFF_MS (or its
 * Retry-After): callers in the window get the same error without a request.
 * If the user signed in or out while the refresh was running, its answer is
 * dropped and the call resolves true so the caller re-reads the stored tokens.
 */
export async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const attempt = (async (): Promise<boolean> => {
    const generation = sessionGeneration;
    const refreshToken = await getRefreshToken();
    if (!refreshToken) return false;
    if (refreshBackoff) {
      if (Date.now() < refreshBackoff.until) throw refreshBackoff.error;
      refreshBackoff = null;
    }
    const base = await getBackendUrl();
    const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), REFRESH_TIMEOUT_MS) : null;
    let waitMs = REFRESH_BACKOFF_MS;
    try {
      let res: Response;
      try {
        res = await fetch(`${base}/api/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ refreshToken }),
          signal: ctl?.signal,
        });
      } catch (e: any) {
        const why = ctl?.signal.aborted ? "timed out" : e?.message || "";
        throw new ApiError(0, `Cannot reach the LSEG backend at ${base} to refresh the session (${why}).`.replace(" ()", ""));
      }
      if (!res.ok) {
        if (isRefreshRejection(res.status)) return generation !== sessionGeneration;
        waitMs = Math.max(waitMs, retryAfterMs(res));
        const body = await parseJson(res);
        throw new ApiError(
          res.status,
          messageFromBody(body, `Session refresh failed (${res.status}); the session is kept, retrying later.`),
          body?.detail
        );
      }
      const tok = await parseJson(res);
      if (!tok?.accessToken || !tok?.refreshToken) {
        throw new ApiError(502, "Session refresh returned no tokens; the session is kept, retrying later.");
      }
      if (generation !== sessionGeneration) return true; // a newer sign-in/out: keep its tokens
      await setTokens(tok.accessToken, tok.refreshToken);
      refreshBackoff = null;
      return true;
    } catch (e) {
      if (e instanceof ApiError && generation === sessionGeneration) {
        refreshBackoff = { until: Date.now() + waitMs, error: e };
      }
      throw e;
    } finally {
      if (timer) clearTimeout(timer);
    }
  })();
  refreshInFlight = attempt;
  const clear = () => {
    if (refreshInFlight === attempt) refreshInFlight = null;
  };
  attempt.then(clear, clear);
  return attempt;
}

const tryRefresh = refreshSession;

// --- Public surface --------------------------------------------------------

export async function login(username: string, password: string): Promise<UserResponse> {
  const tok = await request<{ accessToken: string; refreshToken: string }>("/api/auth/login", {
    method: "POST",
    body: { username, password },
    auth: false,
  });
  newSession();
  await setTokens(tok.accessToken, tok.refreshToken, username);
  // Fetch profile so the UI knows whether a forced password rotation is due.
  return me();
}

// Microsoft 365 single sign-on. Office mints a bootstrap token for the identity
// Excel is already signed into (no password prompt); the backend validates it
// and returns the add-in's own JWTs. Requires the manifest WebApplicationInfo
// block + a host that supports the IdentityAPI requirement set — callers should
// fall back to password login if isSsoSupported() is false or this throws.
export function isSsoSupported(): boolean {
  try {
    return typeof Office !== "undefined" && !!Office.auth?.getAccessToken;
  } catch {
    return false;
  }
}

export async function loginWithMicrosoft(): Promise<UserResponse> {
  // Task pane runs in the shared runtime where Office.auth is available.
  const bootstrap = await Office.auth.getAccessToken({
    allowSignInPrompt: true,
    allowConsentPrompt: true,
    forMSGraphAccess: false,
  });
  const tok = await request<{ accessToken: string; refreshToken: string }>("/api/auth/sso/office", {
    method: "POST",
    body: { token: bootstrap },
    auth: false,
  });
  newSession();
  await setTokens(tok.accessToken, tok.refreshToken);
  const user = await me();
  // Persist the resolved username (mirrors password login) for the session UI.
  await setTokens(tok.accessToken, tok.refreshToken, user.username);
  return user;
}

export async function me(): Promise<UserResponse> {
  return request<UserResponse>("/api/auth/me");
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await request<void>("/api/auth/change-password", {
    method: "POST",
    body: { currentPassword, newPassword },
    // A 401 here means "current password incorrect" (server business logic),
    // NOT an expired token — so don't refresh/retry/clear the session; let the
    // real "Current password is incorrect" message surface.
    retryOn401: false,
  });
}

export async function logout(): Promise<void> {
  newSession();
  await clearTokens();
}

export async function listFunctions(): Promise<FunctionInfo[]> {
  return request<FunctionInfo[]>("/api/functions");
}

export async function evaluate(formula: string): Promise<EvaluateResponse> {
  return request<EvaluateResponse>("/api/evaluate", {
    method: "POST",
    body: { formula },
  });
}
